import { delay, putResolve, select } from "redux-saga/effects";
import {
  FulfillFailure,
  FulfillResponseEnum,
  FulfillResponseV0,
  FulfillSuccessV0,
  Product,
  Payment,
  PaymentError,
  PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { COMPLETE_BUY_VR, HomesReservation } from "redmond";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  vrPurchaseErrorsToString,
} from "@capone/common";
import { buildHomeBookingData } from "../../utils/buildHomeBookingData";
import dayjs from "dayjs";
import pollFinalized from "../../../../api/v0/book/book-flow/pollFinalized";
import { IStoreState } from "../../../../reducers/types";
import {
  getCompleteBuyTotalPaymentProperties,
  getCompleteBuyVacationRentalProperties,
  getSession,
  getTripPurpose,
} from "../../reducer";
import {
  IPollVacationRentalsConfirmationDetails,
  setPollVacationRentalsConfirmationDetailsCallStateFailure,
  setPollVacationRentalsConfirmationDetailsCallStateSuccess,
  setVacationRentalsConfirmationDetails,
} from "../../actions/actions";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";

const getCardDeclineProps = (errors: PurchaseError[]) => {
  const cardError = errors.find(
    (e) => e.Error === PurchaseErrorEnum.PaymentError && (e as PaymentError).value.type === Payment.Card
  );
  if (!cardError) return {};
  const { messageTitle, messageBody } = (cardError as PaymentError).value.value;
  return messageTitle && messageBody
    ? { card_decline_reason_title: messageTitle, card_decline_reason_body: messageBody }
    : {};
};

export function* pollVacationRentalsConfirmationDetailsSaga({
  initialTime,
}: IPollVacationRentalsConfirmationDetails) {
  try {
    const state: IStoreState = yield select();
    const sessionToken = getSession(state);
    const completeBuyVacationRentalProperties =
      getCompleteBuyVacationRentalProperties(state);
    const completeBuyTotalPaymentProperties =
      getCompleteBuyTotalPaymentProperties(state);
    const tripPurpose = getTripPurpose(state);
    if (!sessionToken) {
      throw new Error("Session token is not present.");
    }

    const delayTimes = [1000];
    let pollFailed = false;
    let index = 0;

    while (!pollFailed) {
      yield delay(delayTimes[index]);
      const finalizedCheckedResponse: FulfillResponseV0 = yield pollFinalized(
        sessionToken
      ).catch((e) => {
        // [CMKT-1150] do not error out on network errors, instead retry
        if (e.response.status !== 200) {
          return Promise.resolve({
            data: { result: FulfillResponseEnum.Pending },
            status: 200,
            statusText: "Gateway timeout",
            headers: [],
            config: {},
          });
        }
        return Promise.reject(e);
      });

      switch (finalizedCheckedResponse.FulfillResponse) {
        case FulfillResponseEnum.Failure: {
          pollFailed = true;
          const failedResponse = finalizedCheckedResponse as FulfillFailure;
          const cardDeclineProps = getCardDeclineProps(failedResponse.errors);
          trackEvent({
            eventName: COMPLETE_BUY_VR,
            properties: {
              ...completeBuyVacationRentalProperties.properties,
              ...completeBuyTotalPaymentProperties.properties,
              ...cardDeclineProps,
              success: false,
              failure_reason: vrPurchaseErrorsToString(failedResponse.errors),
              purpose_of_travel: tripPurpose,
              booking_duration: dayjs().diff(initialTime, "seconds", true),
            },
            encryptedProperties: [
              ...completeBuyVacationRentalProperties.encryptedProperties,
            ],
          });
          if (failedResponse.errors.length > 0) {
            yield putResolve(
              setPollVacationRentalsConfirmationDetailsCallStateFailure(
                failedResponse.errors
              )
            );
            return;
          }
          yield putResolve(
            setPollVacationRentalsConfirmationDetailsCallStateFailure([])
          );
          throw new Error(
            "Poll Vacation Rentals Confirmation Details response returned an error and the given error code is not handleable."
          );
        }

        case FulfillResponseEnum.Pending:
          break;
        case FulfillResponseEnum.Success: {
          const finalizedResponse =
            finalizedCheckedResponse as FulfillSuccessV0;
          switch (finalizedResponse.fulfillment.type) {
            case Product.Homes: {
              const reservation = finalizedResponse.fulfillment
                .value as HomesReservation;
              yield putResolve(
                setVacationRentalsConfirmationDetails(reservation)
              );
              yield putResolve(
                setPollVacationRentalsConfirmationDetailsCallStateSuccess()
              );

              const encryptedProperties = [
                ...completeBuyVacationRentalProperties.encryptedProperties,
                finalizedResponse.fulfillment.value.trackingProperties
                  ?.encryptedProperties,
              ].filter(Boolean);

              const finalizedResponseProperties =
                finalizedResponse.fulfillment.value.trackingProperties
                  .properties;
              const hasTotalPaymentPropertiesInResponse =
                !!finalizedResponseProperties.total_card_amount_usd &&
                !!finalizedResponseProperties.total_rewards_amount_usd &&
                finalizedResponseProperties.total_card_amount_usd !== 0 &&
                finalizedResponseProperties.total_rewards_amount_usd !== 0;

              trackEvent({
                eventName: COMPLETE_BUY_VR,
                properties: {
                  ...completeBuyVacationRentalProperties.properties,
                  ...finalizedResponse.fulfillment.value.trackingProperties
                    .properties,
                  ...(hasTotalPaymentPropertiesInResponse ? completeBuyTotalPaymentProperties.properties : {} ),
                  success: true,
                  reservation_id: reservation?.id?.value,
                  vr_type: reservation.listingCollection.toLowerCase(),
                  purpose_of_travel: tripPurpose,
                  booking_duration: dayjs().diff(initialTime, "seconds", true),
                },
                encryptedProperties,
              });

              const homeBookingData = buildHomeBookingData(reservation);

              travelAppInterface.sendEvent({
                name: TravelAppInterfaceEventName.BOOKING_COMPLETE,
                funnel: TravelAppInterfaceFunnelType.HOMES,
                data: homeBookingData,
              });
              return;
            }
            default:
              pollFailed = true;
              yield putResolve(
                setPollVacationRentalsConfirmationDetailsCallStateFailure([])
              );
              throw new Error("Poll Finalized Failed");
          }
        }
        default:
          break;
      }

      // if we want to give up on polling we should instead direct the user to my trips
      if (index >= delayTimes.length) {
        window.location.pathname = "/trips";
      }

      if (index !== delayTimes.length - 1) {
        index++;
      }
    }
  } catch {
    const state: IStoreState = yield select();
    const tripPurpose = getTripPurpose(state);
    trackEvent({
      eventName: COMPLETE_BUY_VR,
      properties: {
        failure_reason: "Poll Confirmation Details Network Call Failed",
        purpose_of_travel: tripPurpose,
      },
      encryptedProperties: [],
    });
    yield putResolve(
      setPollVacationRentalsConfirmationDetailsCallStateFailure([])
    );
  }
}
